/**
 * Cookie consent categories matching the cookie policy.
 * "necessary" is always ON and cannot be toggled off.
 */
export type ConsentCategory = 'necessary' | 'functional' | 'analytical' | 'performance' | 'marketing'

export type ConsentPreferences = Record<ConsentCategory, boolean>

export const CONSENT_CATEGORIES: readonly ConsentCategory[] = [
  'necessary',
  'functional',
  'analytical',
  'performance',
  'marketing',
] as const

export const CATEGORY_INFO: Record<ConsentCategory, { label: string; description: string }> = {
  necessary: {
    label: 'Strictly Necessary',
    description:
      'These cookies are essential for the website to function properly. They enable basic features like page navigation, secure access, and session management. The website cannot operate without them.',
  },
  functional: {
    label: 'Functional',
    description:
      'These cookies enable enhanced functionality and personalization, such as remembering your language preference, region, or customized settings. Without them, some features may not work correctly.',
  },
  analytical: {
    label: 'Statistical / Analytical',
    description:
      'These cookies collect anonymous data about how visitors use the website — such as which pages are visited most often — helping us improve the site experience. All data is aggregated and anonymous.',
  },
  performance: {
    label: 'Performance',
    description:
      'These cookies help us understand and improve website performance by collecting information about load times, server responses, and error rates. They do not identify individual visitors.',
  },
  marketing: {
    label: 'Marketing / Advertising',
    description:
      'These cookies are used to deliver relevant advertisements and track the effectiveness of marketing campaigns. They may be set by third-party advertising partners across different websites.',
  },
}

/** Default state: only necessary cookies are enabled */
export const DEFAULT_CONSENT: ConsentPreferences = {
  necessary: true,
  functional: false,
  analytical: false,
  performance: false,
  marketing: false,
}

/** All categories accepted */
export const ALL_ACCEPTED: ConsentPreferences = {
  necessary: true,
  functional: true,
  analytical: true,
  performance: true,
  marketing: true,
}

/** Consent proof record stored for audit purposes */
export interface ConsentProof {
  timestamp: string
  consentVersion: string
  categories: ConsentPreferences
  userAgent: string
}

/** Current consent version — bump when policy changes */
export const CONSENT_VERSION = '1.0.0'

/** Cookie name for storing consent preferences */
export const CONSENT_COOKIE_NAME = 'pixion-cookie-consent'

/** Cookie expiry in days (12 months ≈ 365 days) */
export const CONSENT_EXPIRY_DAYS = 365

/** LocalStorage keys */
export const CONSENT_STORAGE_KEY = 'pixion-cookie-consent'
export const CONSENT_PROOF_KEY = 'pixion-consent-proof'
